import { useCallback, useContext, useRef } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import type { ILocalCart } from "@swiggy-private/portal-dineout-context";
import {
    AnalyticsContext,
    ObjectNames,
    QrCodeActions,
    ResponseContext,
    ScreenNames,
    SwiggyContextHeader,
    TableContext,
    useDigitalMenuConfig,
    useDigitalMenuTheme,
    usePlApiContextFetch,
} from "@swiggy-private/portal-dineout-context";
import { useConfigTheme } from "./useConfigTheme";
import { useIsV2InApp } from "./useIsV2InApp";
import { useNativeCartSync } from "../components/CartOrders/CartTab/useNativeCartSync";
import {
    API_ENDPOINTS,
    GANDALF_API_HOST,
    DINEOUT_CART_API_HOST,
    LOG_TABLES,
    CART_LABELS,
    SUCCESS_STATUS_CODE,
} from "../constants";
import { getDefaultApiHeaders, updateCartInfoWithTooltipConfig } from "../utils";
import {
    fetchLocalCart,
    hasCartItems,
    filterAvailableCartItems,
    persistLocalCart,
    FetchCartResponse,
} from "../utils/fetchCartHelpers";

export const useFetchCart = () => {
    const fetchPL = usePlApiContextFetch();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { state, dispatch } = useContext(ResponseContext);
    const { restaurantId = "", tableId = "", parentOrderId = "" } = useContext(TableContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const cartImpressionRef = useRef<boolean>(false);
    const { isV2, isV2InApp } = useIsV2InApp();
    const theme = useConfigTheme();
    const { isConfigLoaded, config } = useDigitalMenuConfig();
    const { isThemeUpdating } = useDigitalMenuTheme();
    const useDineoutCartHost = config?.useDineoutCartHostCartPage ?? false;
    const { updateLocalCartInNative, clearLocalCartInNative, refreshMenuInNative } = useNativeCartSync();

    const onImpressionCartPage = useCallback(
        (isError = false) => {
            if (cartImpressionRef.current) return;
            if (!impressionEvent) return;

            impressionEvent({
                category: ScreenNames.CART,
                action: ObjectNames.IMPRESSION_CART_PAGE,
                label: isError ? CART_LABELS.ERROR : CART_LABELS.SUCCESS,
                context: JSON.stringify({
                    restaurant_id: restaurantId,
                    table_id: tableId,
                    parentOrderId,
                    table_no: state?.pin?.data?.tableNo,
                    is_v2_config: isV2,
                    theme,
                }),
            });
            cartImpressionRef.current = true;
        },
        [impressionEvent, restaurantId, tableId, parentOrderId, state?.pin?.data?.tableNo, isV2, theme],
    );

    const fetchCart = useCallback(async () => {
        if (isV2InApp && (isThemeUpdating || !isConfigLoaded)) return;
        if (state.cart?.loading || !swiggyHeaders || !restaurantId || !fetchPL) return;

        dispatch({ type: QrCodeActions.CART_SCREEN_LOADING, payload: {} });

        const finalLocalCart = await fetchLocalCart(isV2InApp, swiggyHeaders);
        if (!hasCartItems(finalLocalCart)) {
            dispatch({ type: QrCodeActions.CART_SCREEN_ERROR, payload: {} });
            return;
        }

        const apiHost = useDineoutCartHost ? DINEOUT_CART_API_HOST : GANDALF_API_HOST;
        const endpoint = useDineoutCartHost ? API_ENDPOINTS.UPDATE_CART_DINEOUT : API_ENDPOINTS.UPDATE_CART;
        const apiHeaders = { ...swiggyHeaders, ...getDefaultApiHeaders(swiggyHeaders) };

        try {
            const data = await fetchPL?.<FetchCartResponse>(
                {
                    apiHost,
                    endpoint,
                    retryCount: 3,
                    bodyParams: { ...finalLocalCart, restaurantId: Number(restaurantId) },
                    options: { method: "POST", headers: apiHeaders as HeadersInit },
                },
                apiHeaders,
                () => {},
                LOG_TABLES,
            );

            if (isEmpty(data) || !data?.success || data?.statusCode !== SUCCESS_STATUS_CODE) {
                dispatch({ type: QrCodeActions.CART_SCREEN_ERROR, payload: {} });
                return;
            }

            const updatedCards = await updateCartInfoWithTooltipConfig(data.success.cards || []);
            const updatedData = { ...data, success: { ...data.success, cards: updatedCards } };

            onImpressionCartPage(false);
            dispatch({
                type: QrCodeActions.CART_SCREEN_SUCCESS,
                payload: { data: updatedData, isChildOrderExist: data.success.isChildOrderExist ?? null },
            });

            if (useDineoutCartHost) {
                const synced = filterAvailableCartItems(updatedCards, finalLocalCart as unknown as ILocalCart);
                dispatch({ type: QrCodeActions.UPDATE_LOCAL_CART, payload: synced });
                persistLocalCart(synced, updatedCards, {
                    updateLocalCartInNative,
                    clearLocalCartInNative,
                    refreshMenuInNative,
                });
            } else {
                dispatch({ type: QrCodeActions.UPDATE_LOCAL_CART, payload: finalLocalCart });
            }
        } catch {
            onImpressionCartPage(true);
            dispatch({ type: QrCodeActions.CART_SCREEN_ERROR, payload: {} });
        }
    }, [
        dispatch,
        fetchPL,
        onImpressionCartPage,
        isV2InApp,
        restaurantId,
        swiggyHeaders,
        state.cart?.loading,
        isConfigLoaded,
        isThemeUpdating,
        useDineoutCartHost,
        updateLocalCartInNative,
        clearLocalCartInNative,
        refreshMenuInNative,
    ]);

    return { fetchCart };
};
